import React from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from 'react-router-dom';

// Layout
import Layout from './components/Layout';
import ToastContainer from './components/ui/Toast';

// Pages
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import TicketsDashboard from './pages/TicketsDashboard';
import SupportDashboard from './pages/SupportDashboard';
import AdminUsers from './pages/AdminUsers';
import ContentReview from './pages/ContentReview';
import UsersPage from './pages/Users';
import UserExplorer from './pages/UserExplorer';
import UserFullView from './pages/UserFullView';
import Finance from './pages/Finance';
import Webhooks from './pages/Webhooks';
import BackgroundVerification from './pages/BackgroundVerification';
import Notifications from './pages/Notifications';
import Referrals from './pages/Referrals';
import AffiliatePartners from './pages/AffiliatePartners';
import ServiceRequests from './pages/ServiceRequests';
import SafetyIncidents from './pages/SafetyIncidents';
import DemoContent from './pages/DemoContent';
import PlaceholderPage from './pages/PlaceholderPage';

// ============================================================================
// Auth Route Guards
// ============================================================================

interface ProtectedRouteProps {
  children: React.ReactNode;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const isAuthenticated = localStorage.getItem('isAuthenticated') === 'true';
  const authToken = localStorage.getItem('authToken');

  if (!isAuthenticated || !authToken) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

const PublicRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const isAuthenticated = localStorage.getItem('isAuthenticated') === 'true';
  const authToken = localStorage.getItem('authToken');

  if (isAuthenticated && authToken) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
};

// ============================================================================
// App Component
// ============================================================================

const App: React.FC = () => {
  return (
    <Router>
      {/* Global Toast Container */}
      <ToastContainer />

      <Routes>
        {/* Public Routes */}
        <Route
          path="/login"
          element={
            <PublicRoute>
              <Login />
            </PublicRoute>
          }
        />

        {/* Protected Routes - Dashboard Layout */}
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <Layout>
                <Dashboard />
              </Layout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <Layout>
                <Dashboard />
              </Layout>
            </ProtectedRoute>
          }
        />

        {/* Customer Support - Main Feature */}
        <Route
          path="/support"
          element={
            <ProtectedRoute>
              <Layout>
                <SupportDashboard />
              </Layout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/support/:ticketId"
          element={
            <ProtectedRoute>
              <Layout>
                <SupportDashboard />
              </Layout>
            </ProtectedRoute>
          }
        />

        {/* Tickets Dashboard (was the old Dashboard) */}
        <Route
          path="/tickets"
          element={
            <ProtectedRoute>
              <Layout>
                <TicketsDashboard />
              </Layout>
            </ProtectedRoute>
          }
        />

        {/* Content Review */}
        <Route
          path="/content-review"
          element={
            <ProtectedRoute>
              <Layout>
                <ContentReview />
              </Layout>
            </ProtectedRoute>
          }
        />

        {/* Website App Demo curation - controls xuman.ai homepage demo feed */}
        <Route
          path="/demo-content"
          element={
            <ProtectedRoute>
              <Layout>
                <DemoContent />
              </Layout>
            </ProtectedRoute>
          }
        />

        {/* Other routes - Placeholders */}
        <Route
          path="/calls"
          element={
            <ProtectedRoute>
              <Layout>
                <PlaceholderPage
                  title="Call Center"
                  description="Manage all voice calls from a centralized dashboard. Coming soon."
                />
              </Layout>
            </ProtectedRoute>
          }
        />

        {/* Users Management */}
        <Route
          path="/users"
          element={
            <ProtectedRoute>
              <Layout>
                <UsersPage />
              </Layout>
            </ProtectedRoute>
          }
        />

        {/* User Explorer - Data Table */}
        <Route
          path="/users/all"
          element={
            <ProtectedRoute>
              <Layout>
                <UserExplorer />
              </Layout>
            </ProtectedRoute>
          }
        />

        {/* User Full View */}
        <Route
          path="/users/:id"
          element={
            <ProtectedRoute>
              <Layout>
                <UserFullView />
              </Layout>
            </ProtectedRoute>
          }
        />

        {/* Finance Dashboard */}
        <Route
          path="/finance"
          element={
            <ProtectedRoute>
              <Layout>
                <Finance />
              </Layout>
            </ProtectedRoute>
          }
        />

        {/* Webhooks Dashboard */}
        <Route
          path="/webhooks"
          element={
            <ProtectedRoute>
              <Layout>
                <Webhooks />
              </Layout>
            </ProtectedRoute>
          }
        />

        {/* Background Checks */}
        <Route
          path="/background-checks"
          element={
            <ProtectedRoute>
              <Layout>
                <BackgroundVerification />
              </Layout>
            </ProtectedRoute>
          }
        />

        {/* Service Requests */}
        <Route
          path="/service-requests"
          element={
            <ProtectedRoute>
              <Layout>
                <ServiceRequests />
              </Layout>
            </ProtectedRoute>
          }
        />

        {/* Notifications */}
        <Route
          path="/notifications"
          element={
            <ProtectedRoute>
              <Layout>
                <Notifications />
              </Layout>
            </ProtectedRoute>
          }
        />

        {/* Referrals */}
        <Route
          path="/referrals"
          element={
            <ProtectedRoute>
              <Layout>
                <Referrals />
              </Layout>
            </ProtectedRoute>
          }
        />

        {/* Affiliate Partners — admin-created partner accounts + their links */}
        <Route
          path="/affiliate-partners"
          element={
            <ProtectedRoute>
              <Layout>
                <AffiliatePartners />
              </Layout>
            </ProtectedRoute>
          }
        />

        {/* Safety Incidents */}
        <Route
          path="/safety-incidents"
          element={
            <ProtectedRoute>
              <Layout>
                <SafetyIncidents />
              </Layout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/analytics"
          element={
            <ProtectedRoute>
              <Layout>
                <PlaceholderPage
                  title="Analytics"
                  description="View support metrics, response times, and satisfaction scores. Coming soon."
                />
              </Layout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/settings"
          element={
            <ProtectedRoute>
              <Layout>
                <PlaceholderPage
                  title="Settings"
                  description="Configure your support dashboard preferences. Coming soon."
                />
              </Layout>
            </ProtectedRoute>
          }
        />

        {/* Admin Users Management - Requires Admin or Super Admin role */}
        <Route
          path="/admin"
          element={
            <ProtectedRoute>
              <Layout>
                <AdminUsers />
              </Layout>
            </ProtectedRoute>
          }
        />

        {/* Catch-all redirect */}
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </Router>
  );
};

export default App;
